import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlan } from '../../context/PlanContext';

/**
 * Reusable upgrade prompt component
 * Shows when user tries to access a feature not in their plan
 * 
 * Props:
 *   - feature: Feature name for custom messaging
 *   - title: Custom title (optional)
 *   - description: Custom description (optional)
 */
const UpgradePrompt = ({ feature, title, description }: { feature: any; title?: any; description?: any }) => {
  const navigate = useNavigate();
  const { plan, upgradeUrl } = usePlan();

  const featureMessages = {
    team_members: {
      title: 'Team Collaboration krever Team-plan',
      description: 'Oppgrader til Team for å legge til andre advokater på saker og samarbeide i sanntid.',
      requiredPlan: 'TEAM'
    },
    team_dashboard: {
      title: 'Team Dashboard krever Team-plan',
      description: 'Få oversikt over hele teamets arbeid, kapasitet og frister.',
      requiredPlan: 'TEAM'
    },
    document_sharing: {
      title: 'Dokumentdeling krever Team-plan',
      description: 'Del dokumenter med hele teamet eller firmaet.',
      requiredPlan: 'TEAM'
    },
    departments: {
      title: 'Avdelinger krever Enterprise-plan',
      description: 'Organiser teamet ditt i avdelinger med egne tilganger og policies.',
      requiredPlan: 'ENTERPRISE'
    },
    sso: {
      title: 'SSO krever Enterprise-plan',
      description: 'Single Sign-On integrasjon med Microsoft AD, Okta eller Google Workspace.',
      requiredPlan: 'ENTERPRISE'
    },
    default: {
      title: 'Funksjon ikke tilgjengelig',
      description: 'Denne funksjonen krever en høyere plan.',
      requiredPlan: 'TEAM'
    }
  };

  const message = featureMessages[feature] || featureMessages.default;

  return (
    <div className="max-w-2xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-indigo-200 shadow-sm">
      <div className="text-center">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {title || message.title}
        </h3>

        {/* Description */}
        <p className="text-gray-600 mb-4">
          {description || message.description}
        </p>

        {/* Current Plan Badge */}
        <div className="inline-block mb-4">
          <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm font-medium">
            Nåværende plan: {plan?.plan_type || 'FREE'}
          </span>
        </div>

        {/* Upgrade Button */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
          <button
            onClick={() => navigate(upgradeUrl)}
            className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
          >
            Oppgrader til {message.requiredPlan}
          </button>
          
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Gå tilbake
          </button>
        </div>

        {/* Pricing Hint */}
        <p className="text-sm text-gray-500 mt-4">
          Se alle planer og priser på innstillinger
        </p>
      </div>
    </div>
  );
};

export default UpgradePrompt;
